import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Radio, Brain, Music, Shield, Link as LinkIcon } from "lucide-react";
import { toast } from "@/components/ui/sonner";

/**
 * How MIRRORS Works
 * Explains: 432 Hz, Binaural beats, Harmonic Resonance Sync
 * Includes disclaimers and links
 */
export default function HowItWorks() {
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const API_BASE = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

  const handleCheckout = async (packageId) => {
    if (packageId === "free") {
      toast.info("Free tier is active.");
      return;
    }

    try {
      setCheckoutLoading(packageId);
      const response = await fetch(`${API_BASE}/payments/v1/checkout/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin,
        }),
      });
      if (!response.ok) {
        throw new Error("Checkout failed");
      }
      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      toast.error("Unable to start checkout");
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Hero */}
      <Card className="sg-frame">
        <CardContent className="text-center py-8">
          <h1 className="text-3xl font-bold mb-4">
            How MIRRORS Works
          </h1>
          <p className="text-[#ddd] max-w-3xl mx-auto mb-4">
            Every illness, emotion, and state of being has its own <span className="text-[#a4ff2d] font-semibold">harmonic resonance</span>.
          </p>
          <p className="text-[#666] max-w-3xl mx-auto">
            MIRRORS uses cymatics tuned to 432 Hz to resonate specific frequencies that 
            cancel out disharmony — helping you restore balance without medicine or surgery.
          </p>
        </CardContent>
      </Card>

      {/* Core Principle */}
      <Card className="sg-frame border-2 border-[#a4ff2d]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Radio className="text-[#a4ff2d]" size={24} />
            Sound Design Meets Frequency Science
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-[#ddd] text-lg">
            GODMODE SOUND is a premium audio platform built around precise frequency generation, 
            binaural beat synthesis, and immersive sound design.
          </p>
          
          <div className="bg-[#0a0a0a] p-6 rounded-lg border border-[#333]">
            <div className="text-[#a4ff2d] font-bold text-xl mb-3 text-center">
              How It Works
            </div>
            <p className="text-[#ddd] text-center mb-4">
              Our tools generate precise audio frequencies and binaural beat patterns 
              that you can customize, layer, and experience through headphones.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mt-6">
            <div className="bg-[#1a1a1a] p-4 rounded text-center">
              <div className="font-semibold mb-2 text-[#a4ff2d]">Select</div>
              <div className="text-sm text-[#999]">Choose from hundreds of frequencies and presets</div>
            </div>
            <div className="bg-[#1a1a1a] p-4 rounded text-center">
              <div className="font-semibold mb-2 text-[#a4ff2d]">Customize</div>
              <div className="text-sm text-[#999]">Layer frequencies, adjust binaural beats, set duration</div>
            </div>
            <div className="bg-[#1a1a1a] p-4 rounded text-center">
              <div className="font-semibold mb-2 text-[#a4ff2d]">Experience</div>
              <div className="text-sm text-[#999]">Listen with headphones for the full binaural effect</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="sg-frame">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Music className="text-[#a4ff2d]" size={24} />
            The 432 Hz Standard
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-[#ddd]">
            432 Hz is an alternative tuning standard used by musicians and audio engineers worldwide.
            All GODMODE SOUND tools default to 432 Hz as the base reference pitch.
          </p>
        </CardContent>
      </Card>

      <Card className="sg-frame">
        <CardHeader>
          <CardTitle>Binaural Beat Technology</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-[#ddd]">
            Binaural beats play two slightly different frequencies in each ear.
            The brain perceives a third beat at the difference frequency.
          </p>
          <div className="bg-[#1a1a1a] p-4 rounded">
            <div className="text-sm text-[#999]">Delta (0.5–4 Hz) · Theta (4–8 Hz) · Alpha (8–13 Hz) · Beta (13–30 Hz) · Gamma (30–100 Hz)</div>
          </div>
        </CardContent>
      </Card>

      <Card className="sg-frame">
        <CardHeader><CardTitle>Best Practices</CardTitle></CardHeader>
        <CardContent>
          <ul className="space-y-2 text-[#ddd]">
            <li className="flex items-start gap-2"><span className="text-[#a4ff2d]">•</span><span><strong>Use headphones:</strong> Binaural beats require separate channels for each ear.</span></li>
            <li className="flex items-start gap-2"><span className="text-[#a4ff2d]">•</span><span><strong>Keep volume low:</strong> 30-50% is optimal.</span></li>
            <li className="flex items-start gap-2"><span className="text-[#a4ff2d]">•</span><span><strong>Start small:</strong> Begin with 8-13 minute sessions.</span></li>
          </ul>
        </CardContent>
      </Card>

      <Card className="sg-frame border-[#666]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield size={24} className="text-[#666]" />
            Disclaimer
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-[#999]">
          <p>GODMODE SOUND is an audio tool for personal entertainment and creative use. It is <strong>not a medical device</strong> and makes no claims regarding health outcomes.</p>
          <p><strong>Do not use</strong> while driving, operating machinery, or if you have a history of seizures or epilepsy.</p>
          <p className="text-xs text-[#666]">For entertainment and personal use only. GODMODE LLC.</p>
        </CardContent>
      </Card>

      {/* Links to GODMODE Ecosystem */}
      {/* Links to GODMODE Ecosystem */}
      <Card className="sg-frame">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="text-[#a4ff2d]" size={24} />
            Connect with GODMODE
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <a
              href="https://instagram.com/vincesinnott"
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 bg-[#1a1a1a] rounded hover:bg-[#252525] transition-colors"
            >
              <div className="font-semibold mb-1">GODMODE SOUND</div>
              <div className="text-xs text-[#666]">
                Follow @vincesinnott on Instagram
              </div>
            </a>

            <a
              href="https://instagram.com/godmodellc"
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 bg-[#1a1a1a] rounded hover:bg-[#252525] transition-colors"
            >
              <div className="font-semibold mb-1">GODMODE LLC</div>
              <div className="text-xs text-[#666]">
                Follow @godmodellc on Instagram
              </div>
            </a>

            <a
              href="mailto:godmodesound@gmail.com?subject=Mailing%20List%20Signup"
              className="p-4 bg-[#1a1a1a] rounded hover:bg-[#252525] transition-colors"
            >
              <div className="font-semibold mb-1">Mailing List</div>
              <div className="text-xs text-[#666]">
                godmodesound@gmail.com
              </div>
            </a>

            <a
              href="mailto:godmodesound@gmail.com?subject=Contact%20Inquiry"
              className="p-4 bg-[#1a1a1a] rounded hover:bg-[#252525] transition-colors"
            >
              <div className="font-semibold mb-1">Contact</div>
              <div className="text-xs text-[#666]">
                godmodesound@gmail.com
              </div>
            </a>
          </div>
        </CardContent>
      </Card>
      <div className="mt-10 flex flex-col items-center gap-4" data-testid="how-pricing-bubbles">
        <div className="text-xs uppercase tracking-[0.35em] text-[#7BA0A3]">Pricing Tiers</div>
        <div className="flex flex-wrap justify-center gap-3">
          {[
            { label: "Free", packageId: "free" },
            { label: "$13/mo", packageId: "tier_13" },
            { label: "$33 Lifetime", packageId: "tier_33" },
          ].map((tier) => (
            <button
              key={tier.label}
              onClick={() => handleCheckout(tier.packageId)}
              disabled={checkoutLoading === tier.packageId}
              className="px-4 py-2 rounded-full border border-[#33ff00]/40 bg-black/70 text-xs uppercase tracking-[0.25em] text-[#33ff00] shadow-[0_0_12px_rgba(51,255,0,0.25)] disabled:opacity-60"
              data-testid={`how-pricing-bubble-${tier.label.replace(/[^a-z0-9]/gi,'').toLowerCase()}`}
            >
              {checkoutLoading === tier.packageId ? "Loading..." : tier.label}
            </button>
          ))}
        </div>
      </div>

      {/* Privacy Policy */}
      <Card className="sg-frame mt-10 border-[#333]" data-testid="privacy-policy-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield size={24} className="text-[#7BA0A3]" />
            Privacy Policy
          </CardTitle>
          <p className="text-xs text-[#666]">Last updated: February 2026</p>
        </CardHeader>
        <CardContent className="space-y-6 text-sm text-[#999] leading-relaxed">
          <div>
            <h3 className="text-[#ddd] font-semibold mb-2">1. Introduction</h3>
            <p>
              GODMODE LLC ("we," "our," or "us") operates the MIRRORS application (the "App"). 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you use our App. By using MIRRORS, you agree to the collection and use of information 
              in accordance with this policy.
            </p>
          </div>

          <div>
            <h3 className="text-[#ddd] font-semibold mb-2">2. Information We Collect</h3>
            <p className="mb-2">We may collect the following types of information:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-[#ddd]">Account Information:</strong> When you create a MIRRORS ID, we collect your display name, email address, and account preferences.</li>
              <li><strong className="text-[#ddd]">Usage Data:</strong> We collect information about how you interact with the App, including frequency selections, session duration, and feature usage.</li>
              <li><strong className="text-[#ddd]">Device Information:</strong> We may collect device type, operating system version, and unique device identifiers to optimize your experience.</li>
              <li><strong className="text-[#ddd]">Payment Information:</strong> If you purchase a subscription, payment processing is handled securely by Stripe. We do not store your full credit card details on our servers.</li>
              <li><strong className="text-[#ddd]">Contacts (Optional):</strong> If you choose to use the Sync feature, the App may request access to your contacts to find other MIRRORS users. Contact data is processed locally and not stored on our servers without your explicit consent.</li>
            </ul>
          </div>

          <div>
            <h3 className="text-[#ddd] font-semibold mb-2">3. How We Use Your Information</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>To provide and maintain the App's core frequency generation functionality.</li>
              <li>To personalize your experience and remember your preferences.</li>
              <li>To process transactions and manage subscriptions.</li>
              <li>To enable social features such as Sync, Push-To-Talk, and frequency sharing.</li>
              <li>To improve and optimize the App's performance and features.</li>
              <li>To communicate with you about updates, security alerts, and support.</li>
            </ul>
          </div>

          <div>
            <h3 className="text-[#ddd] font-semibold mb-2">4. Data Sharing and Disclosure</h3>
            <p>
              We do not sell, trade, or rent your personal information to third parties. We may share 
              information only in the following circumstances:
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong className="text-[#ddd]">Service Providers:</strong> With trusted third-party services (e.g., Stripe for payments, cloud hosting) that assist in operating the App, subject to confidentiality agreements.</li>
              <li><strong className="text-[#ddd]">Legal Requirements:</strong> If required by law, regulation, or legal process.</li>
              <li><strong className="text-[#ddd]">Safety:</strong> To protect the rights, safety, and property of GODMODE LLC, our users, or the public.</li>
            </ul>
          </div>

          <div>
            <h3 className="text-[#ddd] font-semibold mb-2">5. Data Security</h3>
            <p>
              We implement industry-standard security measures to protect your information, including 
              encryption in transit and at rest. However, no method of electronic storage or transmission 
              is 100% secure, and we cannot guarantee absolute security.
            </p>
          </div>

          <div>
            <h3 className="text-[#ddd] font-semibold mb-2">6. Your Rights and Choices</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-[#ddd]">Access & Correction:</strong> You may access, update, or correct your account information at any time through the App.</li>
              <li><strong className="text-[#ddd]">Deletion:</strong> You may request deletion of your account and associated data by contacting us at godmodesound@gmail.com.</li>
              <li><strong className="text-[#ddd]">Opt-Out:</strong> You may opt out of non-essential data collection through the App's settings.</li>
              <li><strong className="text-[#ddd]">Do Not Track:</strong> The App respects "Do Not Track" browser signals where applicable.</li>
            </ul>
          </div>

          <div>
            <h3 className="text-[#ddd] font-semibold mb-2">7. Children's Privacy</h3>
            <p>
              MIRRORS is not intended for use by children under the age of 13. We do not knowingly 
              collect personal information from children under 13. If we discover that a child under 13 
              has provided us with personal information, we will promptly delete it.
            </p>
          </div>

          <div>
            <h3 className="text-[#ddd] font-semibold mb-2">8. Third-Party Services</h3>
            <p>
              The App may contain links to or integrations with third-party services (e.g., Stripe, 
              Google Maps, Apple Maps). These services have their own privacy policies, and we are not 
              responsible for their practices. We encourage you to review their policies before providing 
              any personal information.
            </p>
          </div>

          <div>
            <h3 className="text-[#ddd] font-semibold mb-2">9. Changes to This Policy</h3>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material 
              changes by posting the new policy within the App and updating the "Last updated" date. 
              Your continued use of the App after changes constitutes acceptance of the updated policy.
            </p>
          </div>

          <div>
            <h3 className="text-[#ddd] font-semibold mb-2">10. Contact Us</h3>
            <p>
              If you have any questions or concerns about this Privacy Policy or our data practices, 
              please contact us:
            </p>
            <div className="mt-2 bg-[#1a1a1a] p-4 rounded">
              <p><strong className="text-[#ddd]">GODMODE LLC</strong></p>
              <p>Email: <a href="mailto:godmodesound@gmail.com" className="text-[#a4ff2d] hover:underline">godmodesound@gmail.com</a></p>
              <p>Instagram: <a href="https://instagram.com/godmodellc" target="_blank" rel="noopener noreferrer" className="text-[#a4ff2d] hover:underline">@godmodellc</a></p>
            </div>
          </div>

          <div className="pt-4 border-t border-[#333]">
            <p className="text-xs text-[#666] italic">
              This privacy policy is provided for informational purposes and is designed to comply 
              with applicable privacy laws including CCPA and Apple App Store guidelines. GODMODE LLC 
              reserves the right to modify this policy at any time.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
